/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSession, useAuth } from "../context/AuthContext";
import confetti from "canvas-confetti";
import PromptCopiedModal from "../components/dashboard/PromptCopiedModal";
import { useToast } from "../components/providers/ToastProvider";
import ThemeToggle from "../components/layout/ThemeToggle";
import api from "../services/api";

const isValidSectionValue = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') {
    const trimmed = val.trim().toLowerCase();
    return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined' && trimmed !== 'n/a';
  }
  return true;
};

export default function Activation() {
  const { data: session, update }: any = useSession();
  const { logout } = useAuth();
  const userId = session?.user?.id;
  const isFree = (session?.user as any)?.highest_tier_level === 0;
  const userPhone = session?.user?.phone;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [selectedAudienceIndex, setSelectedAudienceIndex] = useState<number | null>(null);
  const [expandedStates, setExpandedStates] = useState<boolean[]>([]);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [positioningOptions, setPositioningOptions] = useState<any[]>([]);
  const [selectedPositioningIndex, setSelectedPositioningIndex] = useState<number | null>(null);
  const [expandedPositioningStates, setExpandedPositioningStates] = useState<boolean[]>([]);
  const cardRefs3 = useRef<(HTMLDivElement | null)[]>([]);
  const [adCaptions, setAdCaptions] = useState<any[]>([]);
  const [selectedCaptionIndex, setSelectedCaptionIndex] = useState<number | null>(null);
  const [expandedCaptionStates, setExpandedCaptionStates] = useState<boolean[]>([]);
  const cardRefs4 = useRef<(HTMLDivElement | null)[]>([]);
  const [posterPrompts, setPosterPrompts] = useState<any[]>([]);
  const [selectedPosterIndex, setSelectedPosterIndex] = useState<number | null>(null);
  const [expandedPosterStates, setExpandedPosterStates] = useState<boolean[]>([]);
  const cardRefs5 = useRef<(HTMLDivElement | null)[]>([]);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [copiedDescription, setCopiedDescription] = useState("");

  const [usage, setUsage] = useState<any>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [isReinvent, setIsReinvent] = useState(false);

  useEffect(() => {
    const isReset = searchParams.get("reset") === "true";
    const isReinventParam = searchParams.get("reinvent") === "true";
    setIsReinvent(isReset || isReinventParam);
  }, [searchParams]);

  const [campaignStrategy, setCampaignStrategy] = useState("viral_poster");
  const [outputLanguage, setOutputLanguage] = useState("manglish");
  const [offerDetails, setOfferDetails] = useState("");

  const [formData, setFormData] = useState({
    business_name: "",
    product_service_name: "",
    key_features: "",
    price_range: "",
    location: "",
    category: "",
    goal: "Get Leads",
    whatsapp: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const checkStatus = async () => {
      if (!userId) return;

      const queryProfileId = searchParams.get("profileId");

      if (searchParams.get("reset") === "true") {
        setAudiences([]);
        setPositioningOptions([]);
        setAdCaptions([]);
        setPosterPrompts([]);
        setSelectedAudienceIndex(null);
        setSelectedPositioningIndex(null);
        setSelectedCaptionIndex(null);
        setSelectedPosterIndex(null);
        setFormData({
          business_name: "",
          product_service_name: "",
          key_features: "",
          price_range: "",
          location: "",
          category: "",
          goal: "Get Leads",
          whatsapp: "",
        });
        setProfileId(null);
        return;
      }

      if (queryProfileId) {
        try {
          const res = await api.get(`/auth/status?id=${queryProfileId}`);
          if (res.data && res.data.profile) {
            const profile = res.data.profile;
            setProfileId(profile.id);
            setFormData({
              business_name: profile.business_name || "",
              product_service_name: profile.product_service_name || "",
              key_features: profile.key_features || "",
              price_range: profile.price_range || "",
              location: profile.location || "",
              category: profile.category || "",
              goal: profile.goal || "Get Leads",
              whatsapp: profile.whatsapp || "",
            });
            if (profile.selected_strategy) {
              setCampaignStrategy(profile.selected_strategy);
            }
            if (profile.selected_language) {
              setOutputLanguage(profile.selected_language);
            }
            
            const audList = profile.target_audiences?.audiences || [];
            setAudiences(audList);
            if (audList.length > 0 && profile.selected_audience) {
              const audIdx = audList.findIndex(
                (a: any) => a.audience_name === profile.selected_audience.audience_name
              );
              if (audIdx !== -1) setSelectedAudienceIndex(audIdx);
            }

            const posList = profile.positioning_options?.positioning_options || [];
            setPositioningOptions(posList);
            if (posList.length > 0 && profile.selected_positioning) {
              const posIdx = posList.findIndex(
                (p: any) => (p.niche_name || p.angle) === (profile.selected_positioning.niche_name || profile.selected_positioning.angle)
              );
              if (posIdx !== -1) setSelectedPositioningIndex(posIdx);
            }

            const capList = profile.ad_captions?.conversion_angles || profile.ad_captions?.ad_captions || profile.ad_captions || [];
            setAdCaptions(capList);
            if (capList.length > 0 && profile.selected_caption) {
              const capIdx = capList.findIndex(
                (c: any) => (c.poster_headline || c.hook) === (profile.selected_caption.poster_headline || profile.selected_caption.hook)
              );
              if (capIdx !== -1) setSelectedCaptionIndex(capIdx);
            }

            const promptsData = profile.ai_poster_prompts?.poster_prompts || profile.ai_poster_prompts?.design_package || profile.ai_poster_prompts;
            const promptList = promptsData ? (Array.isArray(promptsData) ? promptsData : [promptsData]) : [];
            setPosterPrompts(promptList);
            if (promptList.length > 0 && profile.selected_poster_prompt && !profile.selected_poster_prompt.skipped) {
              const pIdx = promptList.findIndex(
                (p: any) => (p.pro_prompt || p.visual_ai_prompt || p.ai_prompt) === (profile.selected_poster_prompt.pro_prompt || profile.selected_poster_prompt.visual_ai_prompt || profile.selected_poster_prompt.ai_prompt)
              );
              if (pIdx !== -1) setSelectedPosterIndex(pIdx);
            }
          }
        } catch (err) {
          console.error("Failed to load profileId prefill:", err);
        }
        return;
      }

      if (searchParams.get("mode") === "new-poster") {
        try {
          const res = await api.get(`/auth/status?userId=${userId}`);
          if (res.data && res.data.profile) {
            const profile = res.data.profile;
            if (profile.id) {
              setProfileId(profile.id);
            }
            setFormData({
              business_name: profile.business_name || "",
              product_service_name: profile.product_service_name || "",
              key_features: profile.key_features || "",
              price_range: profile.price_range || "",
              location: profile.location || "",
              category: profile.category || "",
              goal: profile.goal || "Get Leads",
              whatsapp: profile.whatsapp || "",
            });
            if (profile.target_audiences) {
              setAudiences(profile.target_audiences.audiences || []);
            }
            if (profile.positioning_options) {
              setPositioningOptions(profile.positioning_options.positioning_options || []);
            }
            setAdCaptions([]);
            setPosterPrompts([]);

            if (profile.target_audiences?.audiences && profile.selected_audience) {
              const audIdx = profile.target_audiences.audiences.findIndex(
                (a: any) => a.audience_name === profile.selected_audience.audience_name
              );
              if (audIdx !== -1) setSelectedAudienceIndex(audIdx);
            }
            if (profile.positioning_options?.positioning_options && profile.selected_positioning) {
              const posIdx = profile.positioning_options.positioning_options.findIndex(
                (p: any) => (p.niche_name || p.angle) === (profile.selected_positioning.niche_name || profile.selected_positioning.angle)
              );
              if (posIdx !== -1) setSelectedPositioningIndex(posIdx);
            }
          }
        } catch (err) {
          console.error("Failed to load new-poster prefill:", err);
        }
        return;
      }

      try {
        const res = await api.get(`/auth/status?userId=${userId}`);
        if (res.data && res.data.profile) {
          const profile = res.data.profile;
          setProfileId(profile.id);
          setFormData({
            business_name: profile.business_name || "",
            product_service_name: profile.product_service_name || "",
            key_features: profile.key_features || "",
            price_range: profile.price_range || "",
            location: profile.location || "",
            category: profile.category || "",
            goal: profile.goal || "Get Leads",
            whatsapp: profile.whatsapp || "",
          });
          if (profile.selected_strategy) {
            setCampaignStrategy(profile.selected_strategy);
          }
          if (profile.selected_language) {
            setOutputLanguage(profile.selected_language);
          }
          
          const audList = profile.target_audiences?.audiences || [];
          setAudiences(audList);
          if (audList.length > 0 && profile.selected_audience) {
            const audIdx = audList.findIndex(
              (a: any) => a.audience_name === profile.selected_audience.audience_name
            );
            if (audIdx !== -1) setSelectedAudienceIndex(audIdx);
          }

          const posList = profile.positioning_options?.positioning_options || [];
          setPositioningOptions(posList);
          if (posList.length > 0 && profile.selected_positioning) {
            const posIdx = posList.findIndex(
              (p: any) => (p.niche_name || p.angle) === (profile.selected_positioning.niche_name || profile.selected_positioning.angle)
            );
            if (posIdx !== -1) setSelectedPositioningIndex(posIdx);
          }

          const capList = profile.ad_captions?.conversion_angles || profile.ad_captions?.ad_captions || profile.ad_captions || [];
          setAdCaptions(capList);
          if (capList.length > 0 && profile.selected_caption) {
            const capIdx = capList.findIndex(
              (c: any) => (c.poster_headline || c.hook) === (profile.selected_caption.poster_headline || profile.selected_caption.hook)
            );
            if (capIdx !== -1) setSelectedCaptionIndex(capIdx);
          }

          const promptsData = profile.ai_poster_prompts?.poster_prompts || profile.ai_poster_prompts?.design_package || profile.ai_poster_prompts;
          const promptList = promptsData ? (Array.isArray(promptsData) ? promptsData : [promptsData]) : [];
          setPosterPrompts(promptList);
          if (promptList.length > 0 && profile.selected_poster_prompt && !profile.selected_poster_prompt.skipped) {
            const pIdx = promptList.findIndex(
              (p: any) => (p.pro_prompt || p.visual_ai_prompt || p.ai_prompt) === (profile.selected_poster_prompt.pro_prompt || profile.selected_poster_prompt.visual_ai_prompt || profile.selected_poster_prompt.ai_prompt)
            );
            if (pIdx !== -1) setSelectedPosterIndex(pIdx);
          }
        }
      } catch (err) {
        console.error("Status check failed:", err);
      }
    };
    checkStatus();
  }, [userId, searchParams]);

  const fetchUsage = async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/auth/membership-usage?userId=${userId}`);
      if (res.data) {
        setUsage(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch membership usage:", err);
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUsage();
    }
  }, [userId]);

  // Delay entrance animation hooks (expand cards after 1.5 seconds)
  useEffect(() => {
    const isStep2Active = audiences.length > 0 && 
      (!positioningOptions || positioningOptions.length === 0) && 
      (!adCaptions || adCaptions.length === 0) && 
      (!posterPrompts || posterPrompts.length === 0) && 
      !isSuccess && !loading;

    if (isStep2Active) {
      setExpandedStates(audiences.map(() => false));
      const timer = setTimeout(() => {
        setExpandedStates(audiences.map((_, idx) => idx === 0));
        setTimeout(() => {
          cardRefs.current[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setExpandedStates([]);
    }
  }, [audiences, positioningOptions, adCaptions, posterPrompts, isSuccess, loading]);

  useEffect(() => {
    const isStep3Active = positioningOptions.length > 0 && 
      (!adCaptions || adCaptions.length === 0) && 
      (!posterPrompts || posterPrompts.length === 0) && 
      !isSuccess && !loading;

    if (isStep3Active) {
      setExpandedPositioningStates(positioningOptions.map(() => false));
      const timer = setTimeout(() => {
        setExpandedPositioningStates(positioningOptions.map((_, idx) => idx === 0));
        setTimeout(() => {
          cardRefs3.current[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setExpandedPositioningStates([]);
    }
  }, [positioningOptions, adCaptions, posterPrompts, isSuccess, loading]);

  useEffect(() => {
    const isStep4Active = adCaptions.length > 0 && 
      (!posterPrompts || posterPrompts.length === 0) && 
      !isSuccess && !loading;

    if (isStep4Active) {
      setExpandedCaptionStates(adCaptions.map(() => false));
      const timer = setTimeout(() => {
        setExpandedCaptionStates(adCaptions.map((_, idx) => idx === 0));
        setTimeout(() => {
          cardRefs4.current[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setExpandedCaptionStates([]);
    }
  }, [adCaptions, posterPrompts, isSuccess, loading]);

  useEffect(() => {
    const isStep5Active = posterPrompts.length > 0 && !isSuccess && !loading;

    if (isStep5Active) {
      setExpandedPosterStates(posterPrompts.map(() => false));
      const timer = setTimeout(() => {
        setExpandedPosterStates(posterPrompts.map((_, idx) => idx === 0));
        setTimeout(() => {
          cardRefs5.current[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setExpandedPosterStates([]);
    }
  }, [posterPrompts, isSuccess, loading]);

  const fireCongratsAnimation = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(function () {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);
      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  };

  useEffect(() => {
    if (userPhone && !formData.whatsapp) {
      setFormData(prev => ({ ...prev, whatsapp: userPhone }));
    }
  }, [userPhone, formData.whatsapp]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    setLoading(true);
    
    const submitData = { ...formData };
    if (campaignStrategy === 'offer_poster' && offerDetails) {
       submitData.key_features = `${submitData.key_features}\n\n[OFFER DETAILS]: ${offerDetails}`;
    }

    try {
      const res = await api.post("/auth/activate", { 
        userId, 
        profileId,
        profile: submitData,
        strategy: campaignStrategy,
        language: outputLanguage
      });

      if (res.data) {
        if (res.data.isFree || isFree) {
          showToast("Profile Saved", "Your business profile has been successfully saved.", "success");
          await update({ points: 100, hasProfile: true });
          navigate("/dashboard");
          return;
        }
        setAudiences(res.data.audiences || []);
        if (res.data.profileId) {
          setProfileId(res.data.profileId);
        }
        fireCongratsAnimation();
      }
    } catch (err: any) {
      console.error("Activation failed:", err);
      const errorData = err.response?.data || {};
      if (err.response?.status === 403 && errorData.limitReached) {
        showToast(
          "Quota Reached",
          `You have reached your combined limit of ${errorData.limit} creations. Please contact your coach to upgrade or request a bonus!`,
          "error"
        );
        fetchUsage();
      } else {
        showToast("Activation Failed", errorData.error || "Something went wrong. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (idx: number) => {
    setSelectedAudienceIndex(idx);
    setExpandedStates((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });

    setTimeout(() => {
      cardRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handleToggleExpand = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedStates((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handlePositioningCardClick = (idx: number) => {
    setSelectedPositioningIndex(idx);
    setExpandedPositioningStates((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });

    setTimeout(() => {
      cardRefs3.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handleTogglePositioningExpand = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPositioningStates((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handleCaptionCardClick = (idx: number) => {
    setSelectedCaptionIndex(idx);
    setExpandedCaptionStates((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });

    setTimeout(() => {
      cardRefs4.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handleToggleCaptionExpand = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCaptionStates((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handlePosterCardClick = (idx: number) => {
    setSelectedPosterIndex(idx);
    setExpandedPosterStates((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });

    setTimeout(() => {
      cardRefs5.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handleTogglePosterExpand = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPosterStates((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handleConfirmSelection = async () => {
    if (selectedAudienceIndex === null) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/activate/select-audience", {
        userId,
        profileId,
        audience: audiences[selectedAudienceIndex]
      });

      if (res.data) {
        setPositioningOptions(res.data.positioning || []);
        fireCongratsAnimation();
      }
    } catch (err) {
      console.error("Selection failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPositioning = async () => {
    if (selectedPositioningIndex === null) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/activate/select-positioning", {
        userId,
        profileId,
        positioning: positioningOptions[selectedPositioningIndex],
        strategy: campaignStrategy,
        language: outputLanguage
      });

      if (res.data) {
        setAdCaptions(res.data.ad_captions || res.data.conversion_angles || []);
        fireCongratsAnimation();
      }
    } catch (err) {
      console.error("Positioning selection failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCaption = async () => {
    if (selectedCaptionIndex === null) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/activate/select-caption", {
        userId,
        profileId,
        caption: adCaptions[selectedCaptionIndex]
      });

      if (res.data) {
        const designPackage = res.data.poster_prompts || res.data.design_package;
        if (designPackage) {
          setPosterPrompts(Array.isArray(designPackage) ? designPackage : [designPackage]);
        } else {
          setPosterPrompts([]);
        }
        fireCongratsAnimation();
      }
    } catch (err: any) {
      console.error("Caption selection failed:", err);
      const errorData = err.response?.data || {};
      if (err.response?.status === 403 && errorData.limitReached) {
        showToast(
          "Quota Reached",
          `You have reached your combined limit of ${errorData.limit} creations. Please contact your coach to upgrade or request a bonus!`,
          "error"
        );
        fetchUsage();
      } else {
        showToast("Generation Failed", errorData.error || "Something went wrong. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPoster = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/activate/select-poster-prompt", {
        userId,
        profileId,
        prompt: selectedPosterIndex !== null ? posterPrompts[selectedPosterIndex] : null,
        source: isReinvent ? "reinvent" : "activation"
      });

      if (res.data) {
        if (isReinvent) {
          await update();
          navigate("/ai-hub");
        } else {
          setIsSuccess(true);
          await update({ 
            mustChangePassword: false,
            points: 100,
            hasProfile: true
          });
        }
      }
    } catch (err) {
      console.error("Poster selection failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndExit = async () => {
    if (!userId) {
      if (isReinvent) {
        navigate("/ai-hub");
      } else {
        logout();
      }
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/activate/save-draft", {
        userId,
        profileId,
        profile: formData,
        strategy: campaignStrategy,
        language: outputLanguage
      });
    } catch (err) {
      console.error("Failed to save draft during exit:", err);
    } finally {
      setLoading(false);
      if (isReinvent) {
        navigate("/ai-hub");
      } else {
        logout();
      }
    }
  };

  const copyToClipboard = (text: string, description: string) => {
    navigator.clipboard.writeText(text);
    setCopiedDescription(description);
    setIsPromptModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center pt-12 pb-8 px-4 selection:bg-primary selection:text-white">
      {/* Header Info */}
      <div className="max-w-4xl w-full mb-8 text-center space-y-3">
        <h1 className="text-3xl md:text-4xl font-black text-on-surface tracking-tighter leading-tight">
          {isReinvent ? "Re-invent " : "Business "}
          <span className="text-primary">
            {isSuccess 
              ? (isReinvent ? "Successful." : "Activated.") 
              : posterPrompts?.length > 0 
                ? "Poster Prompts." 
                : adCaptions?.length > 0 
                  ? "Ad Captions." 
                  : positioningOptions?.length > 0 
                    ? "Selling Angles." 
                    : audiences?.length > 0 
                      ? "Customers." 
                      : "Setup."}
          </span>
        </h1>
        <p className="text-slate-500/70 font-bold text-[13px] leading-relaxed max-w-md mx-auto">
          {isSuccess
            ? "Your foundation is set. You are now authorized to enter the Academy."
            : posterPrompts?.length > 0
              ? "Your visual roadmap is ready. Select an AI prompt for your ad poster and authorize your final activation."
              : adCaptions?.length > 0
                ? "We wrote 3 ad captions for your business. Select your favorite one to get your final poster ideas!"
                : positioningOptions?.length > 0
                  ? "We created 3 simple ways to sell your product or service. Choose the best one to write your first ad caption!"
                  : audiences?.length > 0
                    ? "We found 3 groups of customers who need your product. Select the one you want to target to create your marketing angles!"
                    : "Tell us about your business! Enter your details below to generate your first AI marketing strategy and unlock the Academy."}
        </p>
      </div>

      {/* Theme Toggle & Save/Exit Actions */}
      <div className="fixed top-8 right-8 z-50 flex items-center gap-4">
        <ThemeToggle />
        {!isFree && (
          isReinvent ? (
            <button
              onClick={handleSaveAndExit}
              className="flex items-center gap-2 px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant hover:text-primary hover:bg-primary/5 rounded-2xl transition-all border border-outline-variant/10 shadow-sm bg-surface-container-lowest/80 backdrop-blur-sm cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">arrow_back</span>
              Save and Exit
            </button>
          ) : (
            <button
              onClick={handleSaveAndExit}
              className="flex items-center gap-2 px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-2xl transition-all border border-outline-variant/10 shadow-sm bg-surface-container-lowest/80 backdrop-blur-sm cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">logout</span>
              Save and Exit
            </button>
          )
        )}
      </div>

      {/* Form / Success / Selection Container */}
      <div className={`${(audiences?.length > 0 || positioningOptions?.length > 0 || adCaptions?.length > 0 || posterPrompts?.length > 0) && !isSuccess ? "max-w-6xl" : "max-w-2xl"} w-full bg-surface-container-lowest p-5 md:p-16 rounded-[2rem] md:rounded-[3.5rem] shadow-2xl shadow-primary/5 border border-outline-variant/20 relative overflow-hidden transition-all duration-700`}>
        {isSuccess ? (
          <div className="py-8 flex flex-col items-center text-center space-y-8 animate-in zoom-in-95 duration-700">
            <div className="relative">
              <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-xl shadow-emerald-500/20 relative z-10">
                <span className="material-symbols-outlined text-5xl font-black">verified</span>
              </div>
              <div className="absolute inset-0 bg-emerald-400/30 rounded-full blur-2xl animate-pulse" />
            </div>

            <div className="space-y-2">
              <h2 className="text-4xl font-black text-on-surface tracking-tighter leading-tight">
                {isReinvent ? "Re-invention Complete." : "Setup Complete."}
              </h2>
              <p className="text-sm text-slate-500 font-bold leading-relaxed">
                {isReinvent ? "Strategic Profile Re-invented Successfully" : "Activation Successfully Synchronized"}
              </p>
            </div>

            {!isReinvent && (
              <div className="bg-primary/10 border border-primary/20 px-8 py-4 rounded-[2rem] flex items-center gap-3">
                <span className="material-symbols-outlined text-primary text-2xl">military_tech</span>
                <span className="text-primary font-black text-xs uppercase tracking-[0.2em]">Acquired +100 Bonus XP</span>
              </div>
            )}

            <div className="max-w-md space-y-4">
              <p className="text-sm text-slate-500 font-bold leading-relaxed">
                {isReinvent
                  ? "Your reinvented strategic profile is now live. All generated target audiences, positioning angles, and ad creative captions have been saved for your reference."
                  : <>Your strategic profile is now live. All your generated target audiences, positioning angles, and ad creative captions have been saved to your <span className="text-primary font-black">Business Profile</span> for easy access.</>}
              </p>
              <p className="text-xs text-slate-400 font-medium italic">
                {isReinvent ? "Ready to view in the AI Hub?" : "Ready to enter the Academy dashboard?"}
              </p>
            </div>

            <button
              onClick={() => navigate(isReinvent ? "/ai-hub" : "/dashboard")}
              className="w-full py-6 ai-colors text-white rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] shadow-[0_0_30px_-5px_rgba(243,82,195,0.3)] hover:shadow-[0_0_40px_-5px_rgba(172,88,197,0.5)] hover:translate-y-[-4px] active:scale-95 transition-all cursor-pointer"
            >
              {isReinvent ? "Return to AI Hub" : "Enter Dashboard"}
            </button>
          </div>
        ) : loading ? (
          <>
            {/* Moving Colors Background */}
            <div className="absolute inset-0 z-0 bg-background animate-in fade-in duration-700 pointer-events-none">
              <div className="absolute -top-[30%] -left-[10%] w-[80%] h-[80%] bg-indigo-400/70 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '6s' }} />
              <div className="absolute -bottom-[30%] -right-[10%] w-[90%] h-[90%] bg-sky-400/70 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '8s', animationDirection: 'reverse' }} />
              <div className="absolute top-[10%] left-[20%] w-[60%] h-[60%] bg-pink-400/60 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '5s' }} />
            </div>
            
            {/* Glassmorphic overlay */}
            <div className="absolute inset-0 z-10 bg-background/30 backdrop-blur-[50px] animate-in fade-in duration-700 pointer-events-none" />

            {/* Content on top of glass */}
            <div className="relative z-20 py-24 flex flex-col items-center justify-center space-y-6 text-center max-w-sm mx-auto px-4 animate-in zoom-in duration-700">
              <div className="relative w-28 h-28 mb-4 flex items-center justify-center">
                <div className="absolute inset-[-20%] animate-[spin_4s_linear_infinite] mix-blend-multiply opacity-90">
                  <div className="absolute top-0 left-0 w-20 h-20 bg-indigo-500 rounded-full blur-lg" />
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-sky-500 rounded-full blur-lg" />
                  <div className="absolute top-4 right-4 w-16 h-16 bg-pink-500 rounded-full blur-lg" />
                </div>

                <div className="absolute inset-0 animate-[spin_3s_linear_infinite_reverse] mix-blend-multiply opacity-90">
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-indigo-400 rounded-full blur-md" />
                  <div className="absolute top-0 right-0 w-16 h-16 bg-sky-400 rounded-full blur-md" />
                  <div className="absolute top-0 left-2 w-16 h-16 bg-pink-400 rounded-full blur-md" />
                </div>

                <span className="material-symbols-outlined text-5xl text-white relative z-10 drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)] animate-pulse" style={{ animationDuration: '1.5s' }}>auto_awesome</span>
              </div>
              
              <h3 className="text-3xl font-black text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)] tracking-tighter">
                {posterPrompts?.length > 0 ? "Setting up dashboard..." : adCaptions?.length > 0 ? "Creating poster ideas..." : positioningOptions?.length > 0 ? "Writing ad captions..." : audiences?.length > 0 ? "Creating selling options..." : "Finding target customers..."}
              </h3>
            </div>
          </>
        ) : posterPrompts?.length > 0 ? (
          <div className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
            <div className="grid grid-cols-1 gap-8">
              {posterPrompts.map((p, idx) => {
                const isExpanded = expandedPosterStates[idx] || false;
                const isSelected = selectedPosterIndex === idx;
                return (
                  <div
                    key={idx}
                    ref={(el) => { cardRefs5.current[idx] = el; }}
                    onClick={() => handlePosterCardClick(idx)}
                    className={`text-left p-8 md:p-10 rounded-[2.5rem] border-2 transition-all duration-300 w-full flex flex-col gap-0 group relative overflow-hidden cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 scale-[1.02] -translate-y-1"
                        : "border-outline-variant/10 bg-surface-container-lowest shadow-md hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 hover:bg-primary/[0.02]"
                    }`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePosterCardClick(idx);
                      }
                    }}
                  >
                    {isSelected && (
                      <div className="absolute top-8 right-8 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white shadow-lg z-20">
                        <span className="material-symbols-outlined text-base font-black">check</span>
                      </div>
                    )}

                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none group-hover:bg-primary/10 transition-colors duration-700" />

                    <div className={`flex items-center justify-between gap-4 w-full pr-12 pb-6 transition-colors duration-500 border-b ${isExpanded ? "border-outline-variant/10" : "border-transparent"}`}>
                      <div className="space-y-2 w-full">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-black uppercase tracking-[0.2em] text-primary">Option {idx + 1}</span>
                          {(isValidSectionValue(p.pillar) || idx === 2) && (
                            <div 
                              className="px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border transition-all duration-300 shadow-sm shrink-0"
                              style={
                                (p.pillar && p.pillar.includes("DREAM"))
                                  ? { backgroundColor: "rgba(14, 165, 233, 0.12)", borderColor: "rgba(14, 165, 233, 0.3)", color: "#0284c7" }
                                  : (p.pillar && p.pillar.includes("PAIN"))
                                  ? { backgroundColor: "rgba(244, 63, 94, 0.12)", borderColor: "rgba(244, 63, 94, 0.3)", color: "#e11d48" }
                                  : (idx === 2 || (p.pillar && p.pillar.includes("TRANSFORMATION")))
                                  ? { backgroundColor: "rgba(139, 92, 246, 0.12)", borderColor: "rgba(139, 92, 246, 0.3)", color: "#7c3aed" }
                                  : { backgroundColor: "rgba(16, 185, 129, 0.12)", borderColor: "rgba(16, 185, 129, 0.3)", color: "#059669" }
                              }
                            >
                              {p.pillar ? p.pillar.replace("THE ", "") : "TRANSFORMATION"}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                            <span className="material-symbols-outlined text-lg">psychology</span>
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">The Visual Strategy</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => handleTogglePosterExpand(idx, e)}
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 border border-outline-variant/10 transition-all duration-300 hover:bg-primary/5 hover:border-primary/20 shrink-0 cursor-pointer relative z-30"
                      >
                        <span className={`material-symbols-outlined text-xl transition-transform duration-500 ${isExpanded ? "rotate-180 text-primary" : "text-slate-400"}`}>
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>

                    <div className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="flex flex-col gap-8 w-full pt-6 relative z-10">
                          <div className="space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              {isValidSectionValue(p.pillar) && (
                                <div 
                                  className="px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all duration-300 shadow-sm"
                                  style={
                                    p.pillar.includes("DREAM")
                                      ? { backgroundColor: "rgba(14, 165, 233, 0.12)", borderColor: "rgba(14, 165, 233, 0.3)", color: "#0284c7" }
                                      : p.pillar.includes("PAIN")
                                      ? { backgroundColor: "rgba(244, 63, 94, 0.12)", borderColor: "rgba(244, 63, 94, 0.3)", color: "#e11d48" }
                                      : p.pillar.includes("TRANSFORMATION")
                                      ? { backgroundColor: "rgba(139, 92, 246, 0.12)", borderColor: "rgba(139, 92, 246, 0.3)", color: "#7c3aed" }
                                      : { backgroundColor: "rgba(16, 185, 129, 0.12)", borderColor: "rgba(16, 185, 129, 0.3)", color: "#059669" }
                                  }
                                >
                                  {p.pillar.replace("THE ", "")}
                                </div>
                              )}
                            </div>
                            {isValidSectionValue(p.description) && (
                              <h3 className="text-xl md:text-2xl font-bold text-slate-700 tracking-tight leading-relaxed max-w-5xl italic bg-slate-50 p-6 rounded-3xl border border-slate-200">
                                {p.description}
                              </h3>
                            )}
                          </div>

                          {isValidSectionValue(p.pro_prompt) && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between px-2">
                                <div className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">Ready-to-Use Visual Code</span>
                                </div>
                              </div>

                              <div className="relative group/prompt overflow-hidden rounded-[2.5rem] border border-white/5 bg-slate-900 shadow-2xl">
                                <div className="p-8 md:p-12 min-h-[120px] flex items-center">
                                  <p className="text-[14px] md:text-base font-medium text-slate-300 leading-relaxed font-mono selection:bg-primary/30">
                                    {p.pro_prompt}
                                  </p>
                                </div>

                                <div className="bg-white/5 border-t border-white/5 p-4 flex justify-end">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(p.pro_prompt, p.description);
                                    }}
                                    className="px-8 py-4 bg-primary text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-3 shadow-xl cursor-pointer"
                                  >
                                    <span className="material-symbols-outlined text-base">content_copy</span>
                                    Copy Prompt
                                  </button>
                                </div>
                              </div>

                              <div className="px-8 py-5 bg-primary/5 rounded-2xl border border-primary/10 max-w-fit">
                                <p className="text-[11px] font-bold text-primary/70 leading-relaxed flex items-center gap-3">
                                  <span className="material-symbols-outlined text-sm">auto_fix_high</span>
                                  Synthesized Strategy: High-conversion visual mapping for {formData.product_service_name}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center gap-6">
              <button
                onClick={handleConfirmPoster}
                disabled={loading}
                className={`w-full max-w-xl py-6 rounded-[2.5rem] font-black text-sm uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${
                  loading
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed border-none opacity-50"
                    : "ai-colors text-white shadow-[0_0_30px_-5px_rgba(243,82,195,0.3)] hover:shadow-[0_0_40px_-5px_rgba(172,88,197,0.5)] hover:translate-y-[-4px] active:scale-95 cursor-pointer"
                }`}
              >
                {loading ? (
                  <span className="material-symbols-outlined animate-spin">sync</span>
                ) : (
                  <span className="material-symbols-outlined">verified_user</span>
                )}
                {loading ? (
                  isReinvent ? "Saving Reinvention..." : "Completing Activation..."
                ) : selectedPosterIndex !== null ? (
                  isReinvent ? "CONFIRM SELECTION & SAVE TO AI HUB" : "CONFIRM SELECTION & UNLOCK ACADEMY"
                ) : (
                  isReinvent ? "SKIP SELECTION & SAVE TO AI HUB" : "SKIP SELECTION & UNLOCK ACADEMY"
                )}
              </button>
            </div>
          </div>
        ) : adCaptions?.length > 0 ? (
          <div className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
            <div className="grid grid-cols-1 gap-8">
              {adCaptions.map((caption, idx) => {
                const isExpanded = expandedCaptionStates[idx] || false;
                const isSelected = selectedCaptionIndex === idx;
                return (
                  <div
                    key={idx}
                    ref={(el) => { cardRefs4.current[idx] = el; }}
                    onClick={() => handleCaptionCardClick(idx)}
                    className={`text-left p-8 md:p-10 rounded-[2.5rem] border-2 transition-all duration-300 w-full flex flex-col gap-0 group relative overflow-hidden cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 scale-[1.02] -translate-y-1"
                        : "border-outline-variant/10 bg-surface-container-lowest shadow-md hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 hover:bg-primary/[0.02]"
                    }`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCaptionCardClick(idx);
                      }
                    }}
                  >
                    {isSelected && (
                      <div className="absolute top-8 right-8 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white shadow-lg">
                        <span className="material-symbols-outlined text-base font-black">check</span>
                      </div>
                    )}

                    <div className={`flex items-center justify-between gap-4 w-full pr-12 pb-6 transition-colors duration-500 border-b ${isExpanded ? "border-outline-variant/10" : "border-transparent"}`}>
                      <div className="space-y-2">
                        <span className="text-xs font-black uppercase tracking-[0.2em] text-primary">Option {idx + 1}</span>
                        <h3 className="text-3xl font-black text-on-surface tracking-tight leading-tight">{caption.angle_type}</h3>
                      </div>
                      
                      <button
                        type="button"
                        onClick={(e) => handleToggleCaptionExpand(idx, e)}
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 border border-outline-variant/10 transition-all duration-300 hover:bg-primary/5 hover:border-primary/20 shrink-0 cursor-pointer relative z-30"
                      >
                        <span className={`material-symbols-outlined text-xl transition-transform duration-500 ${isExpanded ? "rotate-180 text-primary" : "text-slate-400"}`}>
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>

                    <div className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full pt-6">
                          <div className="space-y-6">
                            {isValidSectionValue(caption.poster_headline) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">view_carousel</span>
                                  Poster Headline (Quick Hook)
                                </label>
                                <p className="text-xl font-black text-slate-800 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-200">&quot;{caption.poster_headline}&quot;</p>
                              </div>
                            )}

                            {isValidSectionValue(caption.emotional_logic) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-indigo-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">psychology</span>
                                  Why people will want it
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-indigo-50 p-4 rounded-2xl border border-indigo-100">{caption.emotional_logic}</p>
                              </div>
                            )}
                          </div>

                          <div className="space-y-6">
                            {isValidSectionValue(caption.visual_theme) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-emerald-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">image</span>
                                  Poster Image Idea
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-emerald-50 p-4 rounded-2xl border border-emerald-100">{caption.visual_theme}</p>
                              </div>
                            )}

                            {isValidSectionValue(caption.whatsapp_status_caption) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">chat</span>
                                  WhatsApp Status Message
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-primary/5 p-4 rounded-2xl border border-primary/10 whitespace-pre-wrap">{caption.whatsapp_status_caption}</p>
                              </div>
                            )}
                            
                            {isValidSectionValue(caption.tempt_to_buy_cta) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-red-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">touch_app</span>
                                  What to tell them to do (CTA)
                                </label>
                                <p className="text-sm font-black text-red-600 leading-relaxed bg-red-50 p-4 rounded-2xl border border-red-100">{caption.tempt_to_buy_cta}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center gap-6">
              <button
                onClick={handleConfirmCaption}
                disabled={selectedCaptionIndex === null || loading}
                className={`relative overflow-hidden w-full max-w-md py-6 rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${
                  loading 
                    ? "text-slate-800 shadow-xl border border-white/50 cursor-default" 
                    : "ai-colors text-white shadow-[0_0_30px_-5px_rgba(243,82,195,0.3)] hover:shadow-[0_0_40px_-5px_rgba(172,88,197,0.5)] hover:translate-y-[-4px] active:scale-95 disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
                }`}
              >
                {loading && (
                  <>
                    <div className="absolute inset-[-50%] z-0 bg-background pointer-events-none">
                      <div className="absolute inset-0 animate-[spin_3s_linear_infinite] mix-blend-multiply opacity-90">
                        <div className="absolute top-0 left-0 w-[60%] h-[60%] bg-indigo-500 rounded-full blur-md" />
                        <div className="absolute bottom-0 right-0 w-[60%] h-[60%] bg-sky-500 rounded-full blur-md" />
                        <div className="absolute top-1/4 right-1/4 w-[50%] h-[50%] bg-pink-500 rounded-full blur-md" />
                      </div>
                    </div>
                    <div className="absolute inset-0 z-10 bg-background/40 backdrop-blur-md pointer-events-none" />
                  </>
                )}

                <div className="relative z-20 flex items-center gap-3">
                  {loading ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">sync</span>
                      Activating...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">verified</span>
                      Use This Caption & Create Poster Ideas
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
        ) : positioningOptions?.length > 0 ? (
          <div className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
            <div className="grid grid-cols-1 gap-8">
              {positioningOptions.map((opt, idx) => {
                const isExpanded = expandedPositioningStates[idx] || false;
                const isSelected = selectedPositioningIndex === idx;
                return (
                  <div
                    key={idx}
                    ref={(el) => { cardRefs3.current[idx] = el; }}
                    onClick={() => handlePositioningCardClick(idx)}
                    className={`text-left p-8 md:p-10 rounded-[2.5rem] border-2 transition-all duration-300 w-full flex flex-col gap-0 group relative overflow-hidden cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 scale-[1.02] -translate-y-1"
                        : "border-outline-variant/10 bg-surface-container-lowest shadow-md hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 hover:bg-primary/[0.02]"
                    }`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePositioningCardClick(idx);
                      }
                    }}
                  >
                    {isSelected && (
                      <div className="absolute top-8 right-8 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white shadow-lg">
                        <span className="material-symbols-outlined text-base font-black">check</span>
                      </div>
                    )}

                    <div className={`flex items-center justify-between gap-4 w-full pr-12 pb-6 transition-colors duration-500 border-b ${isExpanded ? "border-outline-variant/10" : "border-transparent"}`}>
                      <div className="space-y-2">
                        <span className="text-xs font-black uppercase tracking-[0.2em] text-primary">Option {idx + 1}</span>
                        <h3 className="text-3xl font-black text-on-surface tracking-tight leading-tight">{opt.niche_name}</h3>
                      </div>
                      
                      <button
                        type="button"
                        onClick={(e) => handleTogglePositioningExpand(idx, e)}
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 border border-outline-variant/10 transition-all duration-300 hover:bg-primary/5 hover:border-primary/20 shrink-0 cursor-pointer relative z-30"
                      >
                        <span className={`material-symbols-outlined text-xl transition-transform duration-500 ${isExpanded ? "rotate-180 text-primary" : "text-slate-400"}`}>
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>

                    <div className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full pt-6">
                          <div className="space-y-6">
                            {isValidSectionValue(opt.positioning_hook) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">campaign</span>
                                  How to Catch Their Attention
                                </label>
                                <p className="text-sm font-black text-slate-800 leading-relaxed bg-slate-50 p-4 rounded-2xl italic">&quot;{opt.positioning_hook}&quot;</p>
                              </div>
                            )}

                            {isValidSectionValue(opt.the_niche_logic) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-indigo-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">track_changes</span>
                                  Why target this group?
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-indigo-50 p-4 rounded-2xl border border-indigo-100">{opt.the_niche_logic}</p>
                              </div>
                            )}
                          </div>

                          <div className="space-y-6">
                            {isValidSectionValue(opt.why_it_wins) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-emerald-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">workspace_premium</span>
                                  Why this works so well
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-emerald-50 p-4 rounded-2xl border border-emerald-100">{opt.why_it_wins}</p>
                              </div>
                            )}

                            {isValidSectionValue(opt.whatsapp_status_teaser) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">chat</span>
                                  Example WhatsApp message
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-primary/5 p-4 rounded-2xl border border-primary/10">{opt.whatsapp_status_teaser}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center gap-6">
              <button
                onClick={handleConfirmPositioning}
                disabled={selectedPositioningIndex === null || loading}
                className={`relative overflow-hidden w-full max-w-md py-6 rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${
                  loading 
                    ? "text-slate-800 shadow-xl border border-white/50 cursor-default" 
                    : "ai-colors text-white shadow-[0_0_30px_-5px_rgba(243,82,195,0.3)] hover:shadow-[0_0_40px_-5px_rgba(172,88,197,0.5)] hover:translate-y-[-4px] active:scale-95 disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed"
                }`}
              >
                {loading && (
                  <>
                    <div className="absolute inset-[-50%] z-0 bg-background pointer-events-none">
                      <div className="absolute inset-0 animate-[spin_3s_linear_infinite] mix-blend-multiply opacity-90">
                        <div className="absolute top-0 left-0 w-[60%] h-[60%] bg-indigo-500 rounded-full blur-md" />
                        <div className="absolute bottom-0 right-0 w-[60%] h-[60%] bg-sky-500 rounded-full blur-md" />
                        <div className="absolute top-1/4 right-1/4 w-[50%] h-[50%] bg-pink-500 rounded-full blur-md" />
                      </div>
                    </div>
                    <div className="absolute inset-0 z-10 bg-background/40 backdrop-blur-md pointer-events-none" />
                  </>
                )}

                <div className="relative z-20 flex items-center gap-3">
                  {loading ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">sync</span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">verified</span>
                      Use This Option & Create Ad Caption
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
        ) : audiences?.length > 0 ? (
          <div className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
            <div className="grid grid-cols-1 gap-8">
              {audiences.map((audience, idx) => {
                const isExpanded = expandedStates[idx] || false;
                const isSelected = selectedAudienceIndex === idx;
                return (
                  <div
                    key={idx}
                    ref={(el) => { cardRefs.current[idx] = el; }}
                    onClick={() => handleCardClick(idx)}
                    className={`text-left p-8 md:p-10 rounded-[2.5rem] border-2 transition-all duration-300 w-full flex flex-col gap-0 group relative overflow-hidden cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 scale-[1.02] -translate-y-1"
                        : "border-outline-variant/10 bg-surface-container-lowest shadow-md hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 hover:bg-primary/[0.02]"
                    }`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCardClick(idx);
                      }
                    }}
                  >
                    {isSelected && (
                      <div className="absolute top-8 right-8 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white shadow-lg">
                        <span className="material-symbols-outlined text-base font-black">check</span>
                      </div>
                    )}

                    <div className={`flex items-center justify-between gap-4 w-full pr-12 pb-6 transition-colors duration-500 border-b ${isExpanded ? "border-outline-variant/10" : "border-transparent"}`}>
                      <div className="space-y-2">
                        <span className="text-xs font-black uppercase tracking-[0.2em] text-primary">Customer Group {idx + 1}</span>
                        <h3 className="text-3xl font-black text-on-surface tracking-tight leading-tight">{audience.audience_name}</h3>
                      </div>
                      
                      <button
                        type="button"
                        onClick={(e) => handleToggleExpand(idx, e)}
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 border border-outline-variant/10 transition-all duration-300 hover:bg-primary/5 hover:border-primary/20 shrink-0 cursor-pointer relative z-30"
                      >
                        <span className={`material-symbols-outlined text-xl transition-transform duration-500 ${isExpanded ? "rotate-180 text-primary" : "text-slate-400"}`}>
                          keyboard_arrow_down
                        </span>
                      </button>
                    </div>

                    <div className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full pt-6">
                          <div className="space-y-6">
                            {isValidSectionValue(audience.identity) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">person</span>
                                  Who they are
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl">{audience.identity}</p>
                              </div>
                            )}

                            {(isValidSectionValue(audience.dual_struggle?.practical) || isValidSectionValue(audience.dual_struggle?.emotional)) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-red-400 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">warning</span>
                                  Their main challenge
                                </label>
                                <div className="p-5 rounded-2xl bg-red-50 space-y-4 border border-red-100">
                                  {isValidSectionValue(audience.dual_struggle?.practical) && (
                                    <div className="space-y-1">
                                      <span className="text-[10px] font-black uppercase tracking-widest text-red-500">Daily life problem</span>
                                      <p className="text-sm font-bold text-slate-700 leading-relaxed">{audience.dual_struggle.practical}</p>
                                    </div>
                                  )}
                                  {isValidSectionValue(audience.dual_struggle?.practical) && isValidSectionValue(audience.dual_struggle?.emotional) && (
                                    <div className="h-px w-full bg-red-200/50" />
                                  )}
                                  {isValidSectionValue(audience.dual_struggle?.emotional) && (
                                    <div className="space-y-1">
                                      <span className="text-[10px] font-black uppercase tracking-widest text-red-500">How they feel about it</span>
                                      <p className="text-sm font-bold text-slate-700 leading-relaxed">{audience.dual_struggle.emotional}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="space-y-6">
                            {isValidSectionValue(audience.dream_state) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-emerald-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">star</span>
                                  What they want to achieve
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-emerald-50 p-4 rounded-2xl border border-emerald-100">{audience.dream_state}</p>
                              </div>
                            )}

                            {(isValidSectionValue(audience.the_skepticism) || isValidSectionValue(audience.skepticism)) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-indigo-500 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">psychology_alt</span>
                                  Why they might doubt you
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-indigo-50 p-4 rounded-2xl border border-indigo-100">{audience.the_skepticism || audience.skepticism}</p>
                              </div>
                            )}

                            {isValidSectionValue(audience.niche_trigger) && (
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                  <span className="material-symbols-outlined text-sm">ads_click</span>
                                  What makes them buy
                                </label>
                                <p className="text-sm font-bold text-slate-600 leading-relaxed bg-primary/5 p-4 rounded-2xl border border-primary/10">{audience.niche_trigger}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center gap-6">
              <button
                onClick={handleConfirmSelection}
                disabled={selectedAudienceIndex === null || loading}
                className="w-full max-w-md py-6 ai-colors text-white rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] shadow-[0_0_30px_-5px_rgba(243,82,195,0.3)] hover:shadow-[0_0_40px_-5px_rgba(172,88,197,0.5)] hover:translate-y-[-4px] active:scale-95 transition-all disabled:opacity-30 flex items-center justify-center gap-3 cursor-pointer disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="material-symbols-outlined animate-spin">sync</span>
                ) : (
                  <span className="material-symbols-outlined">rocket_launch</span>
                )}
                {loading ? "Generating..." : "Confirm & Create Angles"}
              </button>
            </div>
          </div>
        ) : loadingUsage ? (
          <div className="py-24 flex flex-col items-center justify-center space-y-4 text-center">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
            <p className="text-xs font-black uppercase tracking-widest text-slate-500">Checking Quota status...</p>
          </div>
        ) : (usage?.hasReachedLimit && !isFree) ? (
          <div className="relative z-20 py-12 flex flex-col items-center justify-center space-y-6 text-center max-w-md mx-auto px-4 animate-in zoom-in duration-500">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center text-primary relative mb-4">
              <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-75" />
              <span className="material-symbols-outlined text-4xl font-black relative z-10">lock</span>
            </div>
            
            <h3 className="text-3xl font-black text-slate-800 tracking-tighter">
              Quota Limit Reached
            </h3>
            
            <p className="text-sm text-slate-500 font-bold leading-relaxed">
              You have used all of your combined brand strategy and poster prompt creations ({usage.currentUsage} / {usage.totalLimit}).
            </p>

            <div className="p-5 bg-gradient-to-br from-primary/5 to-indigo-500/5 border border-outline-variant/10 rounded-2xl w-full text-left space-y-3">
              <div className="flex justify-between items-center text-xs font-black text-slate-400 uppercase tracking-widest">
                <span>Current Tier</span>
                <span className="text-slate-700">{usage.membership} ({usage.isMonthly ? "Monthly" : "One-time"})</span>
              </div>
              <div className="flex justify-between items-center text-xs font-black text-slate-400 uppercase tracking-widest">
                <span>Extra Creations Awarded</span>
                <span className="text-slate-700">+{usage.extraCreations}</span>
              </div>
              <div className="flex justify-between items-center text-xs font-black text-slate-400 uppercase tracking-widest">
                <span>Total Limit</span>
                <span className="text-slate-700">{usage.totalLimit}</span>
              </div>
            </div>

            <p className="text-xs text-slate-400 font-semibold leading-relaxed">
              Want more quota? Contact your coach or administrator to upgrade your membership tier or request bonus creation credits!
            </p>

            <div className="pt-4 flex gap-4 w-full justify-center">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="px-6 py-3 border border-slate-200 rounded-xl font-black text-[10px] uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <span className="material-symbols-outlined text-base">arrow_back</span>
                Back to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8 w-full text-left">
             {/* Section 1: Campaign Goal (Language & Strategy) */}
            {!isFree && (
              <div className="bg-surface-container p-5 md:p-6 rounded-2xl md:rounded-[2rem] border border-outline-variant/20 shadow-sm space-y-6">
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 px-1">1. Choose Output Language</label>
                  <div className="flex flex-wrap gap-3">
                    {['simple_english', 'manglish', 'malayalam'].map(lang => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setOutputLanguage(lang)}
                        className={`px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${outputLanguage === lang ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-105' : 'bg-surface-container-low border border-outline-variant/20 text-slate-500 hover:bg-surface-container-high'}`}
                      >
                        {lang.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 px-1">2. Select Campaign Strategy</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { id: 'viral_poster', icon: 'rocket_launch', title: 'Viral Poster', desc: 'Max Reach & Hooks' },
                      { id: 'brand_poster', icon: 'diamond', title: 'Brand Poster', desc: 'Trust & Storytelling' },
                      { id: 'offer_poster', icon: 'local_fire_department', title: 'Offer Poster', desc: 'Urgency & Sales' }
                    ].map(strategy => (
                      <button
                        key={strategy.id}
                        type="button"
                        onClick={() => setCampaignStrategy(strategy.id)}
                        className={`p-5 rounded-[2rem] border-2 transition-all flex flex-col gap-2 items-start text-left cursor-pointer ${campaignStrategy === strategy.id ? 'border-primary bg-primary/5 shadow-md scale-[1.02]' : 'border-outline-variant/10 bg-surface-container-lowest hover:border-primary/30'}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${campaignStrategy === strategy.id ? 'bg-primary text-white' : 'bg-surface-container-high text-slate-500'}`}>
                          <span className="material-symbols-outlined text-lg">{strategy.icon}</span>
                        </div>
                        <span className="font-bold text-sm text-on-surface">{strategy.title}</span>
                        <span className="text-[10px] text-slate-500 uppercase tracking-widest font-black">{strategy.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 2: Business & Product Details */}
            <div className={isFree ? "space-y-6 px-1" : "bg-surface-container p-5 md:p-6 rounded-2xl md:rounded-[2rem] border border-outline-variant/20 shadow-sm space-y-6"}>
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 px-1">{isFree ? "Product & Contact Details" : "3. Product & Contact Details"}</label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <input required name="business_name" value={formData.business_name} onChange={handleChange} className={`w-full px-5 md:px-8 py-4 md:py-5 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface`} placeholder="Business Name" />
                <input required name="category" value={formData.category} onChange={handleChange} className={`w-full px-5 md:px-8 py-4 md:py-5 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface`} placeholder="Category (e.g. Real Estate)" />
              </div>

              <input required name="product_service_name" value={formData.product_service_name} onChange={handleChange} className={`w-full px-5 md:px-8 py-4 md:py-5 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface`} placeholder="Product / Service Name" />
              
              <textarea required name="key_features" value={formData.key_features} onChange={handleChange} rows={3} className={`w-full px-5 md:px-8 py-4 md:py-6 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2.5rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface resize-none`} placeholder="Key Features (What makes it special?)" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <input required name="price_range" value={formData.price_range} onChange={handleChange} className={`w-full px-5 md:px-8 py-4 md:py-5 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface`} placeholder="Price Range" />
                <input required name="location" value={formData.location} onChange={handleChange} className={`w-full px-5 md:px-8 py-4 md:py-5 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface`} placeholder="Customer Location" />
              </div>

              <input required name="whatsapp" value={formData.whatsapp} onChange={handleChange} className={`w-full px-5 md:px-8 py-4 md:py-5 ${isFree ? 'bg-surface-container' : 'bg-surface-container-lowest'} border border-outline-variant rounded-2xl md:rounded-[2rem] outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold text-sm text-on-surface`} placeholder="WhatsApp Number" />
            </div>

            {/* Progressive Disclosure: Offer Details */}
            {campaignStrategy === 'offer_poster' && (
              <div className="bg-red-50/50 p-5 md:p-6 rounded-2xl md:rounded-[2rem] border border-red-100 shadow-sm space-y-4 animate-in slide-in-from-top-4 fade-in duration-500 w-full text-left">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-red-500 px-1 flex items-center gap-2"><span className="material-symbols-outlined text-sm">warning</span> Offer Details (Scarcity & Deadlines)</label>
                <textarea required={campaignStrategy === 'offer_poster'} value={offerDetails} onChange={(e) => setOfferDetails(e.target.value)} rows={2} className="w-full px-5 md:px-8 py-4 md:py-6 bg-surface-container-lowest border border-red-200 rounded-2xl md:rounded-[2.5rem] outline-none focus:border-red-400 focus:ring-4 focus:ring-red-500/10 transition-all font-bold text-sm text-on-surface resize-none" placeholder="e.g. Weekend offer, Rs.599, Only 10 slots available..." />
              </div>
            )}

            <div className="sticky bottom-4 z-50 pt-4 flex flex-col sm:flex-row gap-4 w-full bg-surface-container-lowest/80 backdrop-blur-sm p-2 rounded-3xl">

              <button
                type="submit"
                disabled={loading || isSuccess}
                className={`flex-1 py-6 rounded-[2rem] font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-3 disabled:opacity-50 ${isSuccess ? "bg-emerald-500 text-white cursor-default" : isFree ? "bg-primary text-white hover:translate-y-[-4px] shadow-lg hover:shadow-primary/20 active:scale-95 cursor-pointer" : "ai-colors text-white hover:translate-y-[-4px] shadow-[0_0_30px_-5px_rgba(243,82,195,0.3)] hover:shadow-[0_0_40px_-5px_rgba(172,88,197,0.5)] active:scale-95 cursor-pointer disabled:cursor-not-allowed"}`}
              >
                {loading ? (isFree ? "Saving Profile..." : "Synchronizing...") : (
                  <>
                    <span className="material-symbols-outlined text-lg">{isFree ? "save" : "auto_awesome"}</span>
                    {isFree ? "Activate Profile & Go to Dashboard" : "Generate AI Strategy"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
        <PromptCopiedModal
          isOpen={isPromptModalOpen}
          onClose={() => setIsPromptModalOpen(false)}
          description={copiedDescription}
        />
      </div>
    </div>
  );
}
